import { ContentToken, KeywordMap } from "@/lib/content/types";
import { Definition } from "@tsTypes/definition-schema";
import { MainMetaMapSubTopic } from "@tsTypes/meta-schema";
import { Question } from "@tsTypes/question-schema";
import { Subject } from "@tsTypes/subject-schema";
import { Test } from "@tsTypes/test-schema";
import { RawTopicTestResponse } from "../../app/(core)/ib/[subject]/(withSyllabusBar)/(tests)/topic-tests/[topic]/[testID]/page";
import {
  calculateQuestionTotalMarks,
  renderQuestionArray,
} from "../katex/render";
import { getMainMetaMap } from "./dataFetching";
import { checkIfMultipleChoiceQuestion } from "./helpers";
import { getUserPreferredSubTopicTitle } from "./routing";

// **************************************************************************************** SUBJECTS
export function getCoreSubjects(subjects: Subject[]) {
  return subjects;
}

export function getCoreSubjectNames(subjects: Subject[]) {
  return getCoreSubjects(subjects).map((s) => s.Name);
}

// **************************************************************************************** QUESTIONS
export function arrangeQuestions(data: Question[]) {
  return data.sort((a, b) => {
    const hasTotalMarksA = ![
      "questions.text-options",
      "questions.image-options",
    ].includes(a.QuestionStyle[0].__component);
    const hasTotalMarksB = ![
      "questions.text-options",
      "questions.image-options",
    ].includes(b.QuestionStyle[0].__component);

    if (!hasTotalMarksA && hasTotalMarksB) {
      return -1;
    } else if (hasTotalMarksA && !hasTotalMarksB) {
      return 1;
    } else {
      return 0;
    }
  });
}

// **************************************************************************************** KEYWORD MAP (shared by notes, videos, cram sheets)
export function buildKeymap(allSubTopicKeyWords: Definition[]): KeywordMap {
  const map: KeywordMap = {};
  if (allSubTopicKeyWords.length === 0) {
    return map;
  }
  // sort words by longest strings so that component terms are rendered before their components
  allSubTopicKeyWords.sort((a, b) => {
    return b.Term.length - a.Term.length;
  });
  allSubTopicKeyWords.forEach((keyword) => {
    const { Term, Definition } = keyword;
    map[Term.toLowerCase()] = stripParagraphTokens(Definition);
  });
  return map;
}

function stripParagraphTokens(tokens: ContentToken[]): ContentToken[] {
  return tokens
    .map((t) => {
      if (t.type !== "html") return t;
      const stripped = t.value.replace(/<\/?p>/g, "");
      if (!stripped) return null;
      return { ...t, value: stripped };
    })
    .filter((t): t is ContentToken => t !== null);
}

// **************************************************************************************** TESTS
export async function formatTest(
  test: RawTopicTestResponse,
  withQuestions?: true
) {
  const mainMetaMap = await getMainMetaMap();
  let questionTypes: string[] = [];
  let mcMarks = 0;
  let laMarks = 0;
  test.RelatedQuestions2.forEach((q) => {
    const totalMarks = calculateQuestionTotalMarks(q);
    if (checkIfMultipleChoiceQuestion(q)) {
      mcMarks === 0 && questionTypes.push("MC");
      mcMarks += totalMarks;
    } else {
      laMarks === 0 && questionTypes.push("LA");
      laMarks += totalMarks;
    }
  });
  const final = {
    documentId: test.documentId,
    relatedTopicDocumentId: test.RelatedTopic.documentId,
    RelatedSubTopics: test.RelatedTopic.RelatedSubTopics.reduce(
      (acc: Test["RelatedSubTopics"], st) => {
        const subTopicMeta = mainMetaMap[st.documentId] as MainMetaMapSubTopic;
        acc[st.documentId] = getUserPreferredSubTopicTitle(subTopicMeta);
        return acc;
      },
      {}
    ),
    RelatedQuestions: withQuestions
      ? arrangeQuestions(renderQuestionArray(test.RelatedQuestions2))
      : undefined,
    QuestionSource: test.QuestionSource,
    Number: test.Number,
    IsHigherLevel: test.IsHigherLevel,
    Breakdown: {
      numQuestions: test.RelatedQuestions2.length,
      mcMarks: mcMarks,
      laMarks: laMarks,
    },
    questionTypes,
  };
  return final as Test;
}
